// Orchestrates a one-rendition HLS → single-file download, entirely client-side.
//
//   parse playlist
//     → (init segment, if fMP4)
//     → for each segment, sequentially:
//          fetch (≤maxAttempts, backoff) → [AES-128 decrypt] → [transmux TS | passthrough fMP4] → sink.write
//          onProgress(done, total)
//     → sink.close()
//
// Sequential (not parallel) on purpose: the sink is an ordered byte stream and
// the transmuxer is stateful, so segments must be appended in playlist order.
// Any unrecoverable fetch error, an abort signal, or an unsupported playlist
// (SAMPLE-AES) tears down via sink.abort() so no half-written file lingers.

import { parseM3u8, type M3u8ByteRange, type M3u8Key } from './parseM3u8';
import { createTransmuxer, type Transmuxer } from './transmux';
import type { FileSink } from './fileSink';
import {
  decryptAes128Cbc,
  fetchKeyBytes,
  importAesKey,
  ivForSegment,
} from './aesDecrypt';

export interface DownloadResult {
  ok: boolean;
  segments: number;
  bytesWritten: number;
  error?: string;
  canceled?: boolean;
}

export interface DownloadHlsLevelArgs {
  playlistUrl: string;
  fetchImpl: typeof fetch;
  sink: FileSink;
  signal?: AbortSignal;
  onProgress?: (done: number, total: number) => void;
  /** Injectable for tests; defaults to the lazy mux.js transmuxer. */
  makeTransmuxer?: () => Promise<Transmuxer>;
  maxAttempts?: number;
  backoffMs?: number;
}

class AbortError extends Error {
  constructor() { super('aborted'); this.name = 'AbortError'; }
}

function rangeHeader(br: M3u8ByteRange): string {
  return `bytes=${br.offset}-${br.offset + br.length - 1}`;
}

async function fetchWithRetry<T>(
  url: string,
  fetchImpl: typeof fetch,
  opts: { attempts: number; backoffMs: number; signal?: AbortSignal; byteRange?: M3u8ByteRange },
  extract: (res: Response) => Promise<T>,
): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= opts.attempts; attempt += 1) {
    if (opts.signal?.aborted) throw new AbortError();
    try {
      const init: RequestInit = {};
      if (opts.signal) init.signal = opts.signal;
      if (opts.byteRange) init.headers = { Range: rangeHeader(opts.byteRange) };
      const res = await fetchImpl(url, init);
      if (!res.ok) throw new Error(`fetch failed (${res.status})`);
      return await extract(res);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') throw err;
      lastErr = err;
      if (attempt < opts.attempts && opts.backoffMs > 0) {
        await new Promise((r) => setTimeout(r, opts.backoffMs * attempt));
      }
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('fetch failed');
}

function fetchBytes(
  url: string,
  fetchImpl: typeof fetch,
  opts: { attempts: number; backoffMs: number; signal?: AbortSignal; byteRange?: M3u8ByteRange },
): Promise<Uint8Array> {
  return fetchWithRetry(url, fetchImpl, opts, async (res) => new Uint8Array(await res.arrayBuffer()));
}

function fetchText(
  url: string,
  fetchImpl: typeof fetch,
  opts: { attempts: number; backoffMs: number; signal?: AbortSignal },
): Promise<string> {
  return fetchWithRetry(url, fetchImpl, opts, (res) => res.text());
}

export async function downloadHlsLevel(args: DownloadHlsLevelArgs): Promise<DownloadResult> {
  const {
    playlistUrl,
    fetchImpl,
    sink,
    signal,
    onProgress,
    makeTransmuxer = createTransmuxer,
    maxAttempts = 3,
    backoffMs = 500,
  } = args;

  let bytesWritten = 0;
  const write = async (bytes: Uint8Array): Promise<void> => {
    await sink.write(bytes);
    bytesWritten += bytes.byteLength;
  };
  const fail = async (error: string): Promise<DownloadResult> => {
    await sink.abort();
    return { ok: false, segments: 0, bytesWritten, error };
  };
  const cancel = async (): Promise<DownloadResult> => {
    await sink.abort();
    return { ok: false, segments: 0, bytesWritten, canceled: true };
  };

  try {
    if (signal?.aborted) return await cancel();

    // ---- playlist ----
    const playlistText = await fetchText(playlistUrl, fetchImpl, { attempts: maxAttempts, backoffMs, signal });
    const parsed = parseM3u8(playlistText, playlistUrl);

    if (parsed.unsupported) return await fail(parsed.unsupported);

    const total = parsed.segments.length;

    // ---- decryption key (AES-128 only; SAMPLE-AES already rejected above) ----
    let cryptoKey: CryptoKey | null = null;
    let key: M3u8Key | undefined;
    if (parsed.key && parsed.key.method === 'AES-128') {
      key = parsed.key;
      const rawKey = await fetchKeyBytes(parsed.key.uri, fetchImpl, maxAttempts, backoffMs);
      cryptoKey = await importAesKey(rawKey);
    }

    // ---- init segment (fMP4 only) ----
    if (parsed.isFmp4 && parsed.initSegmentUrl) {
      if (signal?.aborted) return await cancel();
      const initBytes = await fetchBytes(parsed.initSegmentUrl, fetchImpl, {
        attempts: maxAttempts,
        backoffMs,
        signal,
        ...(parsed.initByteRange ? { byteRange: parsed.initByteRange } : {}),
      });
      await write(initBytes);
    }

    // ---- transmuxer (TS only) ----
    const transmuxer = parsed.isFmp4 ? null : await makeTransmuxer();

    // ---- segments (sequential, in playlist order) ----
    for (let i = 0; i < parsed.segments.length; i += 1) {
      if (signal?.aborted) return await cancel();
      const seg = parsed.segments[i];
      let bytes = await fetchBytes(seg.url, fetchImpl, {
        attempts: maxAttempts,
        backoffMs,
        signal,
        ...(seg.byteRange ? { byteRange: seg.byteRange } : {}),
      });

      if (cryptoKey && key) {
        bytes = await decryptAes128Cbc(bytes, cryptoKey, ivForSegment(key, seg.seq));
      }

      if (transmuxer) {
        for (const out of transmuxer.push(bytes)) await write(out);
      } else {
        await write(bytes);
      }

      onProgress?.(i + 1, total);
    }

    // ---- flush transmuxer tail ----
    if (transmuxer) {
      for (const out of transmuxer.flush()) await write(out);
    }

    await sink.close();
    return { ok: true, segments: total, bytesWritten };
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') return await cancel();
    return await fail(err instanceof Error ? err.message : 'download failed');
  }
}
